import { type VariantProps, cva } from "class-variance-authority";
import * as React from "react";

import { cn } from "@/lib/utils";

const tonalSurfaceVariants = cva("border", {
  variants: {
    tone: {
      neutral: "border-border bg-card text-card-foreground",
      success:
        "border-badge-green/24 bg-badge-green/10 text-badge-green-foreground",
      warning:
        "border-badge-amber/24 bg-badge-amber/10 text-badge-amber-foreground",
      danger: "border-badge-red/24 bg-badge-red/10 text-badge-red-foreground",
      info: "border-badge-sky/24 bg-badge-sky/10 text-badge-sky-foreground",
    },
  },
  defaultVariants: {
    tone: "neutral",
  },
});

const alertVariants = cva(
  "group/alert relative grid w-full gap-0.5 rounded-lg border px-2.5 py-2 text-left text-sm has-data-[slot=alert-action]:relative has-data-[slot=alert-action]:pr-18 has-[>svg]:grid-cols-[auto_1fr] has-[>svg]:gap-x-2 *:[svg]:row-span-2 *:[svg]:translate-y-0.5 *:[svg]:text-current *:[svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default: tonalSurfaceVariants({ tone: "neutral" }),
        success: tonalSurfaceVariants({ tone: "success" }),
        warning: tonalSurfaceVariants({ tone: "warning" }),
        danger: tonalSurfaceVariants({ tone: "danger" }),
        destructive: tonalSurfaceVariants({ tone: "danger" }),
        info: tonalSurfaceVariants({ tone: "info" }),
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

function Alert({
  className,
  variant,
  ...props
}: React.ComponentProps<"div"> & VariantProps<typeof alertVariants>) {
  return (
    <div
      data-slot="alert"
      role="alert"
      className={cn(alertVariants({ variant }), className)}
      {...props}
    />
  );
}

function AlertTitle({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="alert-title"
      className={cn(
        "font-medium group-has-[>svg]/alert:col-start-2 [&_a]:underline [&_a]:underline-offset-3 [&_a]:hover:text-foreground",
        className,
      )}
      {...props}
    />
  );
}

function AlertDescription({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="alert-description"
      className={cn(
        "text-sm text-balance text-muted-foreground md:text-pretty [&_a]:underline [&_a]:underline-offset-3 [&_a]:hover:text-foreground [&_p:not(:last-child)]:mb-4",
        className,
      )}
      {...props}
    />
  );
}

function AlertAction({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="alert-action"
      className={cn("absolute top-2 right-2", className)}
      {...props}
    />
  );
}

export {
  Alert,
  AlertTitle,
  AlertDescription,
  AlertAction,
  alertVariants,
  tonalSurfaceVariants,
};
